import {
  Button,
  Grid,
  IconButton,
  Paper,
  Typography,
  Autocomplete,
  TextField,
} from "@mui/material";
import { Save as SaveIcon } from '@mui/icons-material';
import React, { useState, useEffect, useCallback } from "react";
import WSButton from "../../Components/Button/WSButton";
import WSTextField, { TextType } from "../../Components/TextBox/WSTextBox";
import WSSCheckBoxField from "../../Components/CheckBox/WSCheckBox";
import { WSIcons } from "../../commonFunctions/iIcons";
import WSSelectField from "../../Components/Select/WSSelect";
import WSDateField from "../../Components/DateField/WSDateField";
import { useForm, useFieldArray, FormProvider, Controller, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { callAPI, Method, dateFormat } from "../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import config from "../../Config/clientConfig.json";

export const Expense: React.FC<any> = React.memo((props) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const { showMessage } = useSnackbar();
  
  const [loading, setLoading] = useState(false);
  const [loadingSaveAndNew, setLoadingSaveAndNew] = useState(false);
  const [isRoundOffChecked, setIsRoundOffChecked] = useState(false);
  const [showExtraFields, setShowExtraFields] = useState(false);
  const [products, setProducts] = useState<any[]>([]);
  const [productLoading, setProductLoading] = useState(false);
  const [expenseLoading, setExpenseLoading] = useState(false);

  const genId = () => Date.now().toString(36) + Math.random().toString(36).slice(2);
  const [paymentRows, setPaymentRows] = useState(() => [
    { id: genId(), paymentType: "", amount: "" },
  ]);

  const methods = useForm<any>({
    defaultValues: {
      expenseCategory: "",
      expenseNo: "",
      date: dateFormat(new Date(), "DD-MM-YYYY"),
      round: "0",
      total: "0",
      items: [{
        productId: "",
        qty: "",
        price: "",
        amount: "0"
      }],
      paymentRows: [{ paymentType: "", amount: "" }]
    }
  });

  const { control, handleSubmit, setValue, getValues, formState: { errors }, reset } = methods;
  const { fields, append, remove } = useFieldArray({
    name: "items",
    control,
  });

  // ✅ PERFECT qty * price = amount calculation
  const watchedItems = useWatch({
    control,
    name: "items"
  }) || [];

  useEffect(() => {
    watchedItems.forEach((item: any, index: number) => {
      if (item?.qty && item?.price) {
        const qty = parseFloat(item.qty) || 0;
        const price = parseFloat(item.price) || 0;
        const amount = (qty * price).toFixed(2);
        
        if (getValues(`items.${index}.amount`) !== amount) {
          setValue(`items.${index}.amount`, amount, { shouldValidate: false });
        }
      }
    });
  }, [watchedItems, setValue, getValues]);

  const handleProductSelect = useCallback((selectedProduct: any, index: number) => {
    if (selectedProduct?.id) {
      setValue(`items.${index}.productId`, selectedProduct.id);
      const price = selectedProduct.purchasePrice?.toString() || '0';
      setValue(`items.${index}.price`, price);
    }
  }, [setValue]);

  // ✅ Load Products + Edit Data
  useEffect(() => {
    const loadData = async () => {
      try {
        setProductLoading(true);
        const productResponse = await callAPI("Vyapar/Product", Method.GET);
        setProducts(Array.isArray(productResponse) ? productResponse : []);

        if (isEditMode && id) {
          await loadExpenseData(id);
        }
      } catch (error) {
        console.error("Failed to load data:", error);
        showMessage("Failed to load required data", "error");
      } finally {
        setProductLoading(false);
      }
    };
    loadData();
  }, [showMessage, isEditMode, id]);

  const loadExpenseData = async (expenseId: string) => {
    try {
      setExpenseLoading(true);
      const response = await callAPI(`Vyapar/Expense/${expenseId}`, Method.GET);
      const expense = Array.isArray(response.data || response) ? response.data[0] || response[0] : response.data || response;
      
      const itemsData = expense.items?.map((item: any) => ({
        productId: item.productId || "",
        qty: item.quantity?.toString() || "",
        price: item.unitPrice?.toString() || "",
        amount: (parseFloat(item.quantity || "0") * parseFloat(item.unitPrice || "0")).toFixed(2)
      })) || [{
        productId: "", qty: "", price: "", amount: "0"
      }];

      reset({
        expenseCategory: expense.expenseCategory || "",
        expenseNo: expense.expenseNo || "",
        date: expense.date ? dateFormat(new Date(expense.date), "DD-MM-YYYY") : dateFormat(new Date(), "DD-MM-YYYY"),
        round: expense.roundOff?.toString() || "0",
        total: expense.total?.toString() || "0",
        items: itemsData,
        paymentRows: expense.payments?.map((payment: any) => ({
          paymentType: payment.paymentType || "",
          amount: payment.amount?.toString() || ""
        })) || [{ paymentType: "", amount: "" }]
      });

      setPaymentRows(expense.payments?.map((payment: any) => ({
        id: genId(),
        paymentType: payment.paymentType || "",
        amount: payment.amount?.toString() || ""
      })) || [{ id: genId(), paymentType: "", amount: "" }]);
      
      setIsRoundOffChecked(!!expense.roundOff && expense.roundOff !== 0);
    } catch (error) {
      console.error("Failed to load expense:", error);
      showMessage("Failed to load expense data", "error");
    } finally {
      setExpenseLoading(false);
    }
  };

  const productOptions = products.map((product: any) => ({
    id: product.id,
    label: `${product.name} (${product.itemCode})`,
    value: product.id,
    itemCode: product.itemCode,
    name: product.name,
    purchasePrice: product.purchasePrice || 0,
  }));

  const handleAddRow = () => 
    setPaymentRows((prev) => [...prev, { id: genId(), paymentType: "", amount: "" }]);

  const handleRemoveRow = (idToRemove: any) => 
    setPaymentRows((prev) => prev.filter((r) => r.id !== idToRemove));

  const handleAddItemRow = () => {
    append({
      productId: "",
      qty: "",
      price: "",
      amount: "0"
    });
  };

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);
      const payload = {
        ExpenseCategory: data.expenseCategory,
        ExpenseNo: data.expenseNo || "",
        Date: dateFormat(new Date(data.date), "YYYY-MM-DD"),
        RoundOff: parseFloat(data.round || "0") || 0,
        Items: (getValues("items") || []).map((item: any) => ({
          ProductId: item.productId,
          Quantity: parseFloat(item.qty || "0") || 0,
          UnitPrice: parseFloat(item.price || "0") || 0
        })),
        Payments: (data.paymentRows || []).map((payment: any) => ({
          PaymentType: payment.paymentType || "",
          Amount: parseFloat(payment.amount || "0") || 0
        }))
      };

      console.log("🚀 DTO PERFECT PAYLOAD:", JSON.stringify(payload, null, 2));

      const apiEndpoint = isEditMode && id ? `Vyapar/Expense/${id}` : "Vyapar/Expense";
      const method = isEditMode && id ? Method.PUT : Method.POST;
      
      await callAPI(apiEndpoint, method, payload);
      
      const message = isEditMode ? "Expense updated successfully!" : "Expense created successfully!";
      showMessage(`✅ ${message}`, "success");
      navigate("/expense/list");
    } catch (error: any) {
      console.error("❌ ERROR:", error.response?.data);
      showMessage(error.response?.data?.message || "Failed to save expense", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDataSaveAndNew = async (data: any) => {
    try {
      setLoadingSaveAndNew(true);
      await onSubmit(data);
      reset({
        expenseCategory: "",
        expenseNo: "",
        date: dateFormat(new Date(), "DD-MM-YYYY"),
        round: "0",
        total: "0",
        items: [{
          productId: "",
          qty: "",
          price: "",
          amount: "0"
        }],
        paymentRows: [{ paymentType: "", amount: "" }]
      });
      setPaymentRows([{ id: genId(), paymentType: "", amount: "" }]);
      setShowExtraFields(false);
      setIsRoundOffChecked(false);
      showMessage("Ready for new Expense", "success");
    } catch (error) {
      // Error handled in onSubmit
    } finally {
      setLoadingSaveAndNew(false);
    }
  };

  const handleClose = () => navigate("/expense/list");

  return (
    <FormProvider {...methods}>
      <Grid p={2}>
        <Paper elevation={0} style={{ paddingLeft: 20, paddingRight: 20, marginTop: 0, paddingBottom: 25, borderRadius: "8px" }}>
          <Grid container alignItems="center" justifyContent="space-between" wrap="nowrap">
            <Grid item>
              <Typography fontWeight="bold" variant="h6">
                {isEditMode ? "Edit Expense" : "Expense"}
              </Typography>
            </Grid>
            <Grid item>
              <IconButton onClick={handleClose}>
                <WSIcons.CloseIcon />
              </IconButton>
            </Grid>
          </Grid>

          <Grid container spacing={2} mt={2}>
            {/* Basic Fields */}
            <Grid item xs={12} sm={6}>
              <WSSelectField
                rules={{ required: "Expense Category is required" }}
                control={control}
                id="expenseCategory"
                name="expenseCategory"
                label="Expense Category *"
                options={config.expenseTypeConfig.staticOptionData}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <WSTextField
                label="Expense No *"
                id="expenseNo"
                name="expenseNo"
                control={control}
                rules={{ required: "Expense No is required" }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <WSDateField
                id="date"
                name="date"
                label="Date *"
                dateFormat="DD-MM-YYYY"
                control={control}
                rules={{ required: "Date is required" }}
              />
            </Grid>

            {/* ✅ ITEMS SECTION - qty * price = amount */}
            {fields.map((row, index) => (
              <Grid container spacing={2} key={row.id} alignItems="center" mt={1} ml={1}>
                <Grid item xs={12} sm={3}>
                  <Controller
                    name={`items.${index}.productId`}
                    control={control}
                    rules={{ required: "Item is required" }}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Autocomplete
                        options={productOptions}
                        value={productOptions.find(p => p.id === value) || null}
                        onChange={(_, selected) => {
                          onChange(selected?.id || "");
                          handleProductSelect(selected, index);
                        }}
                        getOptionLabel={(option) => option.label || ''}
                        isOptionEqualToValue={(option, val) => option.id === val}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Item *"
                            error={!!error}
                            helperText={error?.message}
                            size="small"
                          />
                        )}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={2}>
                  <WSTextField
                    label="QTY *"
                    id={`items.${index}.qty`}
                    name={`items.${index}.qty`}
                    control={control}
                    rules={{ required: true, min: 0.01 }}
                    type={TextType.Number}
                  />
                </Grid>

                <Grid item xs={12} sm={2}>
                  <WSTextField
                    label="Price/Unit *"
                    id={`items.${index}.price`}
                    name={`items.${index}.price`}
                    control={control}
                    rules={{ required: true, min: 0 }}
                    type={TextType.Number}
                  />
                </Grid>

                <Grid item xs={12} sm={3}>
                  <WSTextField
                    label="Amount (Auto)"
                    id={`items.${index}.amount`}
                    name={`items.${index}.amount`}
                    control={control}
                    type={TextType.Number}
                    disabled={true}
                  />
                </Grid>

                <Grid item display="flex" justifyContent="center">
                  <WSIcons.DeleteIcon
                    onClick={() => remove(index)}
                    sx={{ color: "red", cursor: "pointer" }}
                  />
                </Grid>
              </Grid>
            ))}

            <Grid item xs={12} sx={{ marginLeft: 1 }}>
              <WSButton
                id="addButton"
                type="button"
                label="+ Add Item"
                size="small"
                onClick={handleAddItemRow}
              />
            </Grid>

            {/* ✅ FIXED PAYMENT SECTION - Proper WSSelectField props */}
            <Grid item xs={12} sm={6}>
              {!showExtraFields ? (
                <>
                  <WSSelectField
                    label="Payment Type"
                    id="paymentType"
                    name="paymentType"
                    control={control}
                    options={config.paymentType.staticOptionData}
                  />
                  <Button
                    variant="text"
                    size="small"
                    sx={{ textTransform: "none", p: 0, mt: 1 }}
                    onClick={() => setShowExtraFields(true)}
                  >
                    + Add Payment Details
                  </Button>
                </>
              ) : (
                <Paper sx={{ p: 2 }}>
                  <Grid container spacing={2}>
                    {paymentRows.map((row, index) => (
                      <React.Fragment key={row.id}>
                        <Grid item xs={12} sm={4}>
                          <WSSelectField
                            label="Payment Type"
                            id={`paymentRows.${index}.paymentType`}
                            name={`paymentRows.${index}.paymentType`}
                            control={control}
                            options={config.paymentType.staticOptionData}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <WSTextField
                            label="Amount"
                            id={`paymentRows.${index}.amount`}
                            name={`paymentRows.${index}.amount`}
                            control={control}
                            type={TextType.Number}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <WSIcons.DeleteIcon
                            onClick={() => handleRemoveRow(row.id)}
                            sx={{ cursor: "pointer", color: "red" }}
                          />
                        </Grid>
                      </React.Fragment>
                    ))}
                    <Grid item xs={12}>
                      <Button
                        variant="text"
                        size="small"
                        sx={{ textTransform: "none", p: 0, mt: 1 }}
                        onClick={handleAddRow}
                      >
                        + Add Payment Type
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              )}
            </Grid>

            <Grid item xs={12} sm={2}>
              <WSSCheckBoxField
                label="Round Off"
                id="roundOff"
                name="roundOff"
                control={control}
                onChange={(e) => {
                  setIsRoundOffChecked(e.target.checked);
                  setValue("round", e.target.checked ? "0" : "0");
                }}
                rules={undefined}
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <WSTextField
                label="Round"
                id="round"
                name="round"
                control={control}
                type={TextType.Number}
                disabled={!isRoundOffChecked}
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <WSTextField
                label="Total *"
                id="total"
                name="total"
                control={control}
                rules={{ required: "Total is required", min: 0 }}
                type={TextType.Number}
              />
            </Grid>
          </Grid>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container justifyContent="flex-end" spacing={2} sx={{ mt: 2, paddingRight: 2 }}>
              <Grid item>
                <WSButton
                  id="saveAndNewBtn"
                  type="submit"
                  variant="contained"
                  color="secondary"
                  size="small"
                  label="Save & New"
                  onClick={handleSubmit(handleDataSaveAndNew)}
                  loading={loadingSaveAndNew}
                />
              </Grid>
              <Grid item>
                <WSButton
                  id="saveBtn"
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="small"
                  label="Save"
                  startIcon={<SaveIcon />}
                  loading={loading}
                />
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Grid>
    </FormProvider>
  );
});

export default Expense;
